import React, { useRef, useState } from 'react';
import html2pdf from 'html2pdf.js';
import { savePDFForm } from './supabaseService';

const LOGO_URL = '/logo.jpg';

const initialObservation = {
  location: '',
  task: '',
  outcome: '', // '✔' or '✖'
  note: '',
  correctionNote: '', // New field for correction note
};

const styles = {
  container: {
    maxWidth: 700,
    margin: '40px auto',
    background: '#fff',
    borderRadius: 16,
    boxShadow: '0 4px 24px rgba(0,0,0,0.10)',
    padding: 24,
    fontFamily: 'Segoe UI, Arial, sans-serif',
  },
  title: {
    textAlign: 'center',
    fontSize: 28,
    fontWeight: 800,
    color: '#b00',
    marginBottom: 8,
    letterSpacing: 1,
  },
  subtitle: {
    textAlign: 'center',
    fontSize: 16,
    color: '#222',
    marginBottom: 18,
    fontWeight: 500,
  },
  obsCard: {
    background: '#fafbfc',
    borderRadius: 12,
    boxShadow: '0 1px 6px rgba(176,0,0,0.07)',
    padding: 18,
    marginBottom: 18,
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  label: {
    fontWeight: 600,
    fontSize: 15,
    color: '#b00',
    marginBottom: 2,
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    fontSize: 15,
    border: '1.5px solid #b00',
    borderRadius: 8,
    background: '#fff',
    color: '#222',
    marginBottom: 0,
    boxSizing: 'border-box',
  },
  select: {
    width: '100%',
    padding: '10px 12px',
    fontSize: 15,
    border: '1.5px solid #b00',
    borderRadius: 8,
    background: '#fff',
    color: '#222',
    marginBottom: 0,
    boxSizing: 'border-box',
  },
  note: {
    width: '100%',
    minHeight: 40,
    padding: '10px 12px',
    fontSize: 15,
    border: '1.5px solid #b00',
    borderRadius: 8,
    background: '#fff',
    color: '#222',
    resize: 'vertical',
    marginBottom: 0,
  },
  addBtn: {
    background: 'linear-gradient(90deg, #b00 60%, #ff4d4d 100%)',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontWeight: 700,
    fontSize: 16,
    padding: '10px 0',
    margin: '10px 0',
    width: '100%',
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(176,0,0,0.08)',
  },
  removeBtn: {
    background: '#fff',
    color: '#b00',
    border: '1.5px solid #b00',
    borderRadius: 8,
    fontWeight: 700,
    fontSize: 14,
    padding: '6px 0',
    margin: '6px 0',
    width: '100%',
    cursor: 'pointer',
  },
  button: {
    display: 'block',
    margin: '32px auto 0',
    padding: '12px 36px',
    fontSize: 18,
    fontWeight: 600,
    color: '#fff',
    background: 'linear-gradient(90deg, #b00 0%, #222 100%)',
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(176,0,0,0.08)',
    transition: 'background 0.2s',
  },
  logo: {
    width: 90,
    height: 'auto',
    display: 'block',
    margin: '24px auto 0',
    borderRadius: 12,
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
};

export default function EECForm() {
  const [date, setDate] = useState('');
  const [observations, setObservations] = useState([{ ...initialObservation }]);
  const pdfRef = useRef();

  const handleObsChange = (idx, key, value) => {
    setObservations(obs => obs.map((o, i) => i === idx ? { ...o, [key]: value } : o));
  };

  const addObservation = () => {
    setObservations(obs => [...obs, { ...initialObservation }]);
  };

  const removeObservation = idx => {
    setObservations(obs => obs.length > 1 ? obs.filter((_, i) => i !== idx) : obs);
  };

  const generatePDF = async () => {
    try {
      const pdfHtml = `
        <div style="font-family: 'Segoe UI', Arial, sans-serif; background: #fff; padding: 0; margin: 0;">
          <div style="background: linear-gradient(90deg, #b00 0%, #222 100%); padding: 18px 0 12px 0; text-align: center; border-radius: 0 0 18px 18px; box-shadow: 0 2px 12px rgba(176,0,0,0.10);">
            <div style='font-size: 30px; font-weight: 900; color: #fff; letter-spacing: 1.2px; margin-bottom: 2px;'>EEC Observation Report</div>
            <div style='font-size: 18px; color: #fff; font-weight: 500; margin-top: 8px;'>Date: ${date || '<span style="color:#eee">(No date)</span>'}
          </div>
          <div style="padding: 24px;">
            <div style='background: #fafbfc; border-radius: 12px; padding: 20px; border: 1.5px solid #eee;'>
              <div style='font-size: 20px; font-weight: 700; color: #b00; margin-bottom: 16px; border-bottom: 2px solid #b00; padding-bottom: 8px;'>Observations</div>
              <table style='width: 100%; border-collapse: collapse; font-size: 16px; margin-top: 4px;'>
                <thead>
                  <tr style='background: #b00;'>
                    <th style='padding: 10px 8px; color: #fff; font-size: 15px; font-weight: 700; border-bottom: 2px solid #222;'>Location</th>
                    <th style='padding: 10px 8px; color: #fff; font-size: 15px; font-weight: 700; border-bottom: 2px solid #222;'>Task</th>
                    <th style='padding: 10px 8px; color: #fff; font-size: 15px; font-weight: 700; border-bottom: 2px solid #222;'>Outcome</th>
                    <th style='padding: 10px 8px; color: #fff; font-size: 15px; font-weight: 700; border-bottom: 2px solid #222;'>Note</th>
                    <th style='padding: 10px 8px; color: #fff; font-size: 15px; font-weight: 700; border-bottom: 2px solid #222;'>Correction</th>
                  </tr>
                </thead>
                <tbody>
                  ${observations.map((obs, i) => `
                    <tr style='background: ${i%2===0 ? '#fff' : '#f6f6f6'};'>
                      <td style='padding: 10px 8px; color: #222; border-bottom: 1px solid #eee; word-break: break-word; white-space: pre-line; max-width: 220px;'>${obs.location.replace(/\n/g, '<br/>')}</td>
                      <td style='padding: 10px 8px; color: #222; border-bottom: 1px solid #eee; word-break: break-word; white-space: pre-line; max-width: 220px;'>${obs.task.replace(/\n/g, '<br/>')}</td>
                      <td style='padding: 10px 8px; color: #222; border-bottom: 1px solid #eee; text-align: center; font-size: 20px;'>
                       ${obs.outcome === '✔' ? '<span style="color:#4caf50;">✔️</span>' : obs.outcome === '⚠' ? '<span style="color:#fbc02d;">⚠️</span>' : obs.outcome === '✖' ? '<span style="color:#b00;">✖️</span>' : ''}
                      </td>
                      <td style='padding: 10px 8px; color: #222; border-bottom: 1px solid #eee; word-break: break-word; white-space: pre-line; max-width: 220px;'>${obs.note.replace(/\n/g, '<br/>')}</td>
                      <td style='padding: 10px 8px; color: #222; border-bottom: 1px solid #eee; word-break: break-word; white-space: pre-line; max-width: 220px;'>${obs.correctionNote.replace(/\n/g, '<br/>')}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </div>
            <img src='${LOGO_URL}' alt='Logo' style='width: 90px; height: auto; border-radius: 12px; box-shadow: 0 1px 4px rgba(0,0,0,0.08); display:block; margin: 32px auto 0 auto;' />
          </div>
        </div>
      `;

      // Generate PDF as blob
      const pdfBlob = await html2pdf()
        .set({
          margin: 0,
          html2canvas: { scale: 2 },
          jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' },
        })
        .from(pdfHtml)
        .outputPdf('blob');

      // Create filename with form name + date
      const currentDate = new Date().toISOString().split('T')[0];
      const fileName = `EEC_Observation_Report_${currentDate}.pdf`;

      // Save to Firebase
      const formData = {
        formType: 'eec',
        title: 'EEC Observation Report',
        location: 'Not specified',
        createdBy: 'Not specified',
        date: date || currentDate,
        observations: observations
      };

                   await savePDFForm(formData, pdfBlob, fileName);
      
      // Also download locally for user convenience
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      alert('PDF saved to library and downloaded successfully!');
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  // Mobile styles
  const mobileStyle = `
    @media (max-width: 600px) {
      .eec-container { padding: 0 !important; border-radius: 0 !important; box-shadow: none !important; background: #f6f7fb !important; }
      .eec-title { font-size: 1.3rem !important; }
      .eec-obsCard { padding: 10px 4px !important; border-radius: 8px !important; }
      .eec-label { font-size: 13px !important; }
      .eec-input, .eec-select, .eec-note { font-size: 15px !important; padding: 8px !important; border-radius: 8px !important; width: 100% !important; min-width: 0 !important; max-width: 100vw !important; }
      .eec-button, .eec-addBtn, .eec-removeBtn { font-size: 16px !important; padding: 12px 0 !important; border-radius: 10px !important; max-width: 100vw !important; }
    }
  `;

  return (
    <div style={styles.container} className="eec-container">
      <style>{mobileStyle}</style>
      <div style={styles.title} className="eec-title">EEC Observation Report</div>
      <div style={styles.subtitle}>Add all observations below. You can add or remove as many as needed.</div>
      <div style={{ marginBottom: 18 }}>
        <div style={styles.label}>Date:</div>
        <input
          style={styles.input}
          className="eec-input"
          type="date"
          value={date}
          onChange={e => setDate(e.target.value)}
        />
      </div>
      {observations.map((obs, idx) => (
        <div key={idx} style={styles.obsCard} className="eec-obsCard">
          <div style={styles.label} className="eec-label">Location:</div>
          <textarea
            style={{...styles.note, minHeight: 60}}
            className="eec-note"
            value={obs.location}
            onChange={e => handleObsChange(idx, 'location', e.target.value)}
            placeholder="Enter location..."
            rows={3}
          />
          <div style={styles.label} className="eec-label">Task:</div>
          <textarea
            style={{...styles.note, minHeight: 60}}
            className="eec-note"
            value={obs.task}
            onChange={e => handleObsChange(idx, 'task', e.target.value)}
            placeholder="Enter task..."
            rows={3}
          />
          <div style={styles.label} className="eec-label">Outcome:</div>
          <select
            style={styles.select}
            className="eec-select"
            value={obs.outcome}
            onChange={e => handleObsChange(idx, 'outcome', e.target.value)}
          >
            <option value="">Select outcome</option>
            <option value="✔">✔ Correct</option>
            <option value="⚠">⚠ Flagged</option>
            <option value="✖">✖ Incorrect</option>
          </select>
          <div style={styles.label} className="eec-label">Note:</div>
          <textarea
            style={styles.note}
            className="eec-note"
            value={obs.note}
            onChange={e => handleObsChange(idx, 'note', e.target.value)}
            placeholder="Enter note..."
          />
          <div style={styles.label} className="eec-label">Correction Note:</div>
          <textarea
            style={styles.note}
            className="eec-note"
            value={obs.correctionNote}
            onChange={e => handleObsChange(idx, 'correctionNote', e.target.value)}
            placeholder="Enter correction note..."
          />
          <button
            type="button"
            style={styles.removeBtn}
            className="eec-removeBtn"
            onClick={() => removeObservation(idx)}
            disabled={observations.length === 1}
          >
            Remove Observation
          </button>
        </div>
      ))}
      <button type="button" style={styles.addBtn} className="eec-addBtn" onClick={addObservation}>+ Add Observation</button>
      <button style={styles.button} className="eec-button" onClick={generatePDF}>Download PDF</button>
      <img src={LOGO_URL} alt="Logo" style={styles.logo} />
    </div>
  );
}
